import React from "react";
import { MapPin } from "lucide-react";

function classNames(...values) {
  return values.filter(Boolean).join(" ");
}

function getCoordsFromPlace(place) {
  const location = place?.location;
  if (!location?.lat || !location?.lng) return null;

  return { lat: location.lat(), lng: location.lng() };
}

function PlaceAutocomplete({
  value,
  onChange,
  onPlaceSelect,
  placeholder = "",
  id = "",
  name = "",
  className = "",
  variant = "default",
  hasTrailingControl = false,
  inputStyles = null,
  required = false,
}) {
  const containerRef = React.useRef(null);
  const fieldRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const onChangeRef = React.useRef(onChange);
  const onPlaceSelectRef = React.useRef(onPlaceSelect);

  // Keep refs updated without triggering re-initialization
  React.useEffect(() => {
    onChangeRef.current = onChange;
    onPlaceSelectRef.current = onPlaceSelect;
  }, [onChange, onPlaceSelect]);

  React.useEffect(() => {
    if (!inputRef.current || !fieldRef.current || typeof google === "undefined") return;

    let cleanup = null;
    const originalAttachShadow = Element.prototype.attachShadow;
    Element.prototype.attachShadow = function (init) {
      if (this.localName === "gmp-place-autocomplete") {
        const shadow = originalAttachShadow.call(this, { ...init, mode: "open" });

        const defaultStyles = `
          width: 100% !important;
          height: var(--gmp-input-height, 64px) !important;
          border: none !important;
          outline: var(--gmp-input-outline, var(--gmp-input-border, solid 1px #d9e5ef)) !important;
          outline-offset: 0 !important;
          border-radius: var(--gmp-input-radius, 8px) !important;
          background-color: var(--gmp-input-bg, #ffffff) !important;
          font-family: var(--gmp-input-font-family, inter, "Helvetica Neue", Helvetica, sans-serif) !important;
          font-size: var(--gmp-input-font-size, 14px) !important;
          font-weight: 400 !important;
          line-height: var(--gmp-input-line-height, 20.3px) !important;
          color: #125790 !important;
          box-sizing: border-box !important;
          margin: 0 !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
          padding: 1px var(--place-autocomplete-padding-right, 24px) 1px var(--place-autocomplete-padding-left, 48px) !important;
        `;

        const style = document.createElement("style");
        style.textContent = `
          :host {
            contain: layout !important;
            display: block !important;
            width: 100% !important;
            height: ${inputStyles ? "auto" : "var(--gmp-input-height, 64px)"} !important;
            border: none !important;
            border-radius: var(--gmp-input-radius, 8px) !important;
            overflow: visible !important;
            background: transparent !important;
            color: #125790 !important;
            font-family: var(--gmp-input-font-family, inter, "Helvetica Neue", Helvetica, sans-serif) !important;
            font-size: var(--gmp-input-font-size, 14px) !important;
            font-weight: 400 !important;
            letter-spacing: normal !important;
            line-height: var(--gmp-input-line-height, 20.3px) !important;
            outline: none !important;
            margin: 0 !important;
            padding: 0 !important;
            box-sizing: border-box !important;
            transition: border-color 150ms ease, box-shadow 150ms ease !important;
          }

          :host(:focus),
          :host(:focus-visible),
          :host(:focus-within) {
            outline: var(--gmp-host-focus-outline, 2px solid rgba(184, 214, 240, 0.72)) !important;
            outline-offset: 0 !important;
            box-shadow: none !important;
          }

          .autocomplete-icon,
          .clear-button {
            display: none !important;
          }

          input {
            ${inputStyles || defaultStyles}
            outline: none !important;
            border-radius: var(--gmp-input-radius, 8px) !important;
            background-clip: padding-box !important;
            border: none !important;
            box-sizing: border-box !important;
            color: inherit !important;
            font-family: inherit !important;
            font-size: inherit !important;
            font-weight: inherit !important;
            letter-spacing: inherit !important;
            line-height: inherit !important;
            max-width: 100% !important;
            min-width: 0 !important;
          }

          input:focus,
          input:focus-visible {
            outline: none !important;
            box-shadow: none !important;
          }

          .input-container {
            border: none !important;
            border-radius: var(--gmp-input-radius, 8px) !important;
            display: flex !important;
            align-items: center !important;
            height: ${inputStyles ? "auto" : "var(--gmp-input-height, 64px)"} !important;
            overflow: visible !important;
            background: transparent !important;
            box-shadow: none !important;
            box-sizing: border-box !important;
            color: inherit !important;
            font: inherit !important;
            width: 100% !important;
            min-width: 100% !important;
            max-width: 100% !important;
          }

          .input-container > * {
            max-width: 100% !important;
          }

          .input-container:focus,
          .input-container:focus-visible,
          .input-container:focus-within {
            outline: none !important;
            box-shadow: none !important;
          }

          .focus-ring {
            display: none !important;
            opacity: 0 !important;
            border: none !important;
            outline: none !important;
            box-shadow: none !important;
          }

          input::placeholder {
            color: var(--place-autocomplete-placeholder-color, #424C58) !important;
          }

          input:placeholder-shown {
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
          }

          @media (max-width: 768px) {
            .input-container {
              ${inputStyles ? "" : "height: var(--gmp-input-height, 53px) !important;"}
            }

            input {
              font-size: var(--gmp-input-mobile-font-size, 16px) !important;
              line-height: var(--gmp-input-mobile-line-height, 1.31) !important;
              padding-left: var(--uw-place-mobile-padding-left, 16px) !important;
              padding-right: var(--uw-place-mobile-padding-right, 16px) !important;
              ${inputStyles ? "" : "height: var(--gmp-input-height, 53px) !important;"}
            }
          }
        `;
        shadow.appendChild(style);

        return shadow;
      }
      return originalAttachShadow.call(this, init);
    };

    const initAutocomplete = async () => {
      try {
        await google.maps.importLibrary("places");

        const originalInput = inputRef.current;
        const parent = originalInput.parentNode;
        const existingAutocomplete = fieldRef.current.querySelector("gmp-place-autocomplete");
        if (existingAutocomplete) existingAutocomplete.remove();

        let wrapper = parent;
        if (!wrapper.classList.contains("place-autocomplete-field-inner")) {
          wrapper = document.createElement("div");
          wrapper.className = "place-autocomplete-field-inner";
          wrapper.style.cssText = "position: relative; width: 100%; display: block;";
          parent.replaceChild(wrapper, originalInput);
          wrapper.appendChild(originalInput);
        }

        originalInput.style.cssText = `
          position: absolute !important;
          clip: rect(1px, 1px, 1px, 1px) !important;
          clip-path: inset(50%) !important;
          height: 1px !important;
          width: 1px !important;
          overflow: hidden !important;
          white-space: nowrap !important;
          border: 0 !important;
          padding: 0 !important;
          margin: -1px !important;
        `;

        const placeAutocompleteElement = document.createElement("gmp-place-autocomplete");
        placeAutocompleteElement.setAttribute("placeholder", placeholder);
        placeAutocompleteElement.setAttribute("country", "us");
        placeAutocompleteElement.className = classNames(
          "place-autocomplete-host",
          `place-autocomplete-host--${variant}`,
          hasTrailingControl && "place-autocomplete-host--with-trailing-control"
        );
        placeAutocompleteElement.style.cssText = "display: block; width: 100%; border: none; border-radius: 8px; overflow: visible; outline: none; background: transparent;";
        placeAutocompleteElement.style.colorScheme = "none";

        wrapper.appendChild(placeAutocompleteElement);

        placeAutocompleteElement.addEventListener("gmp-select", async (event) => {
          const placePrediction = event.placePrediction || event.detail?.placePrediction;
          if (!placePrediction) return;

          try {
            const place = placePrediction.toPlace?.() || placePrediction;
            await place.fetchFields({ fields: ["formattedAddress", "displayName", "location"] });

            const selectedLocation = place.formattedAddress || place.displayName;
            const coords = getCoordsFromPlace(place);
            if (selectedLocation) {
              originalInput.value = selectedLocation;
              onPlaceSelectRef.current?.(selectedLocation, coords);
            }
          } catch (err) {
            const text = placeAutocompleteElement.value;
            if (text) onPlaceSelectRef.current?.(text, null);
          }
        });

        placeAutocompleteElement.addEventListener("input", () => {
          const currentValue = placeAutocompleteElement.value || "";
          originalInput.value = currentValue;
          onChangeRef.current?.(currentValue);
        });

        cleanup = () => {
          placeAutocompleteElement?.remove();
          Element.prototype.attachShadow = originalAttachShadow;
        };
      } catch (err) {
        // Initialization failed; location autocomplete will be unavailable
      }
    };

    initAutocomplete();
    return () => cleanup?.();
  }, [placeholder, inputStyles, variant, hasTrailingControl]);

  React.useEffect(() => {
    if (!inputRef.current) return;

    const syncValue = (webComponent) => {
      if (!webComponent || webComponent.value === value) return;

      webComponent.value = value || "";
      const internalInput = webComponent.shadowRoot?.querySelector('input');
      if (internalInput) internalInput.value = value || "";
    };

    inputRef.current.value = value;

    const webComponent = inputRef.current.parentNode?.querySelector("gmp-place-autocomplete");
    if (webComponent) {
      syncValue(webComponent);
    } else if (value) {
      const timer = setTimeout(() => {
        const component = inputRef.current?.parentNode?.querySelector("gmp-place-autocomplete");
        if (component) syncValue(component);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [value]);

  return (
    <div
      ref={containerRef}
      className={classNames(
        "place-autocomplete-control",
        `place-autocomplete-control--${variant}`,
        hasTrailingControl && "place-autocomplete-control--with-trailing-control",
        className
      )}
    >
      <MapPin size={16} strokeWidth={2} aria-hidden="true" className="search-field-icon" />
      <div ref={fieldRef} className="place-autocomplete-field">
        <input ref={inputRef} id={id} name={name} placeholder={placeholder} required={required} />
      </div>
    </div>
  );
}

export default PlaceAutocomplete;
